import { reactive } from '@vue/composition-api';
import StoryblokClient from 'storyblok-js-client';

declare global {
  interface Window {
    // eslint-disable-next-line
    storyblokStories: any;
  }
}

type StoryblokEvent = 'customEvent' | 'published' | 'input' | 'change' | 'unpublished' | 'enterEditmode';

export const storyapi = new StoryblokClient({ accessToken: process.env.VUE_APP_STORYBLOK_TOKEN });

export type StoryblokEventManager = {
  addEventListener(event: StoryblokEvent, callback: (args: any) => void | Promise<void>): void;
  removeEventListener(event: StoryblokEvent, callback: (args: any) => void | Promise<void>): void;
};

function createStoryblokEventManager(): StoryblokEventManager {
  const _events = new Map<string, ((args: any) => void | Promise<void>)[]>();

  return {
    addEventListener(event, callback) {
      if (!_events.has(event)) {
        _events.set(event, []);
        window.storyblok.on(event, (args) => {
          const cs = _events.get(event) as ((args: any) => void | Promise<void>)[];
          cs.forEach((c) => c(args));
        });
      }
      const cs = _events.get(event) as ((args: any) => void | Promise<void>)[];
      cs.push(callback);
    },
    removeEventListener(event, callback) {
      if (_events.has(event)) {
        const cs = _events.get(event) as ((args: any) => void | Promise<void>)[];
        _events.set(
          event,
          cs.filter((c) => c !== callback)
        );
      }
    },
  };
}

export const storyblokEventManager = createStoryblokEventManager();

export function isInEditor() {
  return window.storyblok.isInEditor();
}

export async function pingEditor(): Promise<StoryblokBridge> {
  return new Promise((res) => {
    return window.storyblok.pingEditor((instance) => res(instance));
  });
}

// eslint-disable-next-line
export async function getStory(slug: string, version: string | undefined): Promise<any> {
  const path = slug.startsWith('/') ? slug.substring(1) : slug;
  if (process.env.VUE_APP_BUILD_OFFLINE) {
    // this rather hacky way is needed for the file protocol as it isn't possible to use fetch etc.
    const tmpScript = document.createElement('script');
    tmpScript.src = `./storyblok/stories/${path}`;
    document.body.append(tmpScript);
    await new Promise((resolve) => {
      tmpScript.onload = () => resolve();
    });
    tmpScript.remove();
    return window.storyblokStories[slug];
  }
  return (await storyapi.get(`cdn/stories/${path}`, { version })).data.story;
}

// eslint-disable-next-line
export async function getReactiveStory(slug: string, version?: string | undefined) : Promise<[() => void, any]> {
  const ret = reactive({ story: null });
  
  await pingEditor();
  const inEditor = isInEditor();
  const storyVer = version ?? (inEditor ? 'draft' : 'published');
  
  async function inputHandler(e: any) {
    if (e?.story?.slug === slug) ret.story = e.story;
  }
  
  async function changeHandler(e: any) {
    if (e) ret.story = await getStory(slug, storyVer)
  }
  
  function addListeners() {
    storyblokEventManager.addEventListener('input', inputHandler);
    storyblokEventManager.addEventListener('change', changeHandler);
  }
  
  function remListeners() {
    storyblokEventManager.removeEventListener('input', inputHandler);
    storyblokEventManager.removeEventListener('change', changeHandler);
  }
  
  if (inEditor) addListeners();
  ret.story = await getStory(slug, storyVer);

  return [remListeners, ret];
}
